import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import OnDeleteIcon from '../../../icons/OnDeleteIcon';
import {
    clearAppointmentAuditData,
    clearNewAppointment,
    deleteUserCalendarAppointment,
    loadAppointmentAuditData,
    setOpenAppointmentSchedulerPanel,
    setUsersCurrentAppointment,
    updateUserCalendarAppointment
} from '../../../store/calendarService';
import {
    loadLocationsByServiceId,
    loadServiceDetailsByService
} from '../../../store/directusService';
import { setCurrentlySelectedParticipant } from '../../../store/participantService';
import { selectUsersForNames } from '../../../store/userSelectors';
import { loadUsersMinimalDetails } from '../../../store/userService';
import { isTimePast, reverseFormatDate } from '../../../utils/dateFunctions';
import { getConfiguredItems } from '../../../utils/directusFunctions';
import { MAX_URL } from '../../../utils/formValidation/commonConstants';
import { createHash } from '../../../utils/stringUtils';
import { hasRole } from '../../../utils/userRoles';
import AuditAppointmentModal from '../../auditing/auditAppointments/AuditAppointmentModal';
import Button from '../../formElements/Button';
import DateSelect from '../../formElements/DateSelect';
import StaticField from '../../formElements/StaticField';
import Switch from '../../formElements/Switch';
import TextInputField from '../../formElements/TextInputField';
import Panel from '../../ui/panel/Panel';
import AdviserSelect from '../components/AdviserSelect';
import AppointmentTypeSelect from '../components/AppointmentTypeSelect';
import LocationSelect from '../components/LocationSelect';
import MandatoryActivitiesSwitch from '../components/MandatoryActivitiesSwitch';
import StartAndEndTimesSelect from '../components/StartAndEndTimesSelect';
import StartAppointmentRow from '../components/StartAppointmentRow';
import StaticParticipant from '../components/StaticParticipant';
import {
    acceptedRoles,
    canEditAppointment,
    deleteRoles,
    getServiceIds,
    group1
} from '../utils/calendarUtils';
import { validate } from '../validation/validateAppointmentEdit';
import { ADDITIONAL_DETAILS_LIMIT } from '../validation/validationFunctions';

import sidePanel from '../styles/schedulePanel.module.css';

const UserAppointmentEdit = ({
    usersCurrentAppointment,
    currentCalendarOwner,
    currentLocation,
    currentParticipantEntry
}) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        typeId: '',
        userId: '',
        locationId: '',
        startTime: '08:00',
        endTime: '09:00',
        date: String(new Date().toISOString().split('T')[0]),
        serviceId: '',
        participantId: '',
        mandatory: false,
        type: '',
        service: '',
        location: '',
        locationObj: {},
        participant: '',
        remoteCallUrl: '',
        additionalDetails: ''
    };

    const [initialStateOnEntry, setInitialStateOnEntry] = useState({});
    const [newEntry, setNewEntry] = useState(initialState);
    const [adviser, setAdviser] = useState({});
    const [errors, setErrors] = useState({});
    const [filteredAppointmentTypes, setFilteredAppointmentTypes] = useState([]);
    const [isDisabledStartAppointmentButton, setIsDisabledStartAppointmentButton] = useState(false);
    const [isDisabledUpdateAppointmentButton, setIsDisabledUpdateAppointmentButton] =
        useState(false);
    const [isDisabledSaveButton, setIsDisabledSaveButton] = useState(false);
    const [isAppointmentKnown, setIsAppointmentKnown] = useState(false);
    const [isAuditable, setIsAuditable] = useState(false);
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditLabel, setAuditLabel] = useState('');
    const [modalHeadingLabel, setModalHeadingLabel] = useState('');
    const [hash, setHash] = useState('01');
    const [isInPast, setIsInPast] = useState(false);
    const [hasParticipantAccess, setHasParticipantAccess] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const users = useSelector((state) => state.entities.userService.usersBySearch);
    const usersForNames = useSelector(selectUsersForNames);
    const serviceDetailsById = useSelector(
        (state) => state.entities.directusService.serviceDetailsById
    );
    const appointmentTypeDetails = useSelector(
        (state) => state.entities.directusService.appointmentTypeDetails
    );
    const locationsByServiceId = useSelector(
        (state) => state.entities.directusService.locationsByServiceId
    );
    const updatedCurrentAppointment = useSelector(
        (state) => state.entities.calendarService.usersCurrentAppointment
    );
    const openAppointmentSchedulerPanel = useSelector(
        (state) => state.entities.calendarService.openAppointmentSchedulerPanel
    );
    const errorMessage = useSelector((state) => state.entities.formsState.errorMessage);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    // USE EFFECTS
    useEffect(() => {
        if ('location' in usersCurrentAppointment) {
            /* eslint-disable no-unused-vars */
            const { location, ...rest } = usersCurrentAppointment;
            setInitialStateOnEntry(rest);
        }
        let serviceId;
        if (usersCurrentAppointment?.serviceId) {
            serviceId = usersCurrentAppointment.serviceId;

            if (!(serviceId in serviceDetailsById))
                dispatch(loadServiceDetailsByService([serviceId]));

            if (!(serviceId in locationsByServiceId)) {
                dispatch(loadLocationsByServiceId([serviceId]));
            }
        }
        if (currentParticipantEntry && 'firstName' in currentParticipantEntry) {
            dispatch(setCurrentlySelectedParticipant(currentParticipantEntry));

            const { contractId = '' } = currentParticipantEntry;
            if (contractId && appointmentTypeDetails?.length) {
                setFilteredAppointmentTypes(getConfiguredItems(appointmentTypeDetails, contractId));
            }
        }

        const usersServiceIds = getServiceIds(loggedInUser);
        setHasParticipantAccess(usersServiceIds.some((el) => el === serviceId));

        const user = [...users, ...usersForNames].find(
            (el) => el.id === usersCurrentAppointment.userId
        );
        if (user) setAdviser(user);
        else dispatch(loadUsersMinimalDetails([usersCurrentAppointment.userId]));
    }, []);

    useEffect(() => {
        if (newEntry.id || !(usersCurrentAppointment?.serviceId in serviceDetailsById)) return;
        const currentParticipantName = currentParticipantEntry
            ? `${currentParticipantEntry?.firstName || ''} ${currentParticipantEntry?.lastName || ''} ${currentParticipantEntry?.ptCode || ''}`
            : '';

        setNewEntry({
            ...usersCurrentAppointment,
            startTime: usersCurrentAppointment.startTime,
            endTime: usersCurrentAppointment.endTime,
            date: usersCurrentAppointment.date,
            mandatory: usersCurrentAppointment?.mandatory || false,
            service: serviceDetailsById[usersCurrentAppointment.serviceId]?.name,
            participant: currentParticipantName,
            location: currentLocation,
            locationObj: newEntry.locationObj,
            type: usersCurrentAppointment.type
        });
        const status = usersCurrentAppointment?.attendances.find(
            (el) => el.participantId === currentParticipantEntry?.id
        )?.status;
        setIsAppointmentKnown(status !== 'Not known');
        setIsInPast(
            isTimePast(`${usersCurrentAppointment.date}T${usersCurrentAppointment.startTime}`)
        );
        setIsDisabledStartAppointmentButton(!hasRole(acceptedRoles, roles));
        setIsDisabledUpdateAppointmentButton(!hasRole(group1, roles));
    }, [serviceDetailsById]);

    useEffect(() => {
        if (
            !usersCurrentAppointment ||
            !(usersCurrentAppointment?.serviceId in locationsByServiceId)
        )
            return;
        const locationObj =
            locationsByServiceId[usersCurrentAppointment.serviceId].find(
                (el) => el.id === usersCurrentAppointment?.locationId
            ) || {};
        setNewEntry((prev) => ({ ...prev, locationObj }));
    }, [locationsByServiceId]);

    useEffect(() => {
        if (Object.keys(adviser).length) return;
        const user = usersForNames.find((el) => el.id === usersCurrentAppointment.userId);
        if (user) setAdviser(user);
    }, [usersForNames]);

    useEffect(() => {
        setIsDisabledStartAppointmentButton(!hasRole(acceptedRoles, roles));
        setIsDisabledUpdateAppointmentButton(!hasRole(group1, roles));
    }, [updatedCurrentAppointment]);

    useEffect(() => {
        setIsDisabledSaveButton(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === `Appointment ${newEntry.type} has been updated (${hash})`) {
            setIsDisabledSaveButton(false);
            !newEntry.participantId && onPanelClose();
        }
        if (successMessage.includes(`Appointment ${newEntry.type} has been deleted`)) {
            setIsDisabledSaveButton(false);
            onPanelClose();
        }
    }, [successMessage]);

    useEffect(() => {
        if (errorMessage) {
            setIsDisabledSaveButton(false);
        }
    }, [errorMessage]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onPanelClose = () => {
        setNewEntry(initialState);
        dispatch(setUsersCurrentAppointment({}));
        dispatch(setOpenAppointmentSchedulerPanel(false));
        dispatch(clearNewAppointment());
    };

    const onAuditModalClose = (e, reason) => {
        e.stopPropagation();
        if (reason === 'backdropClick') return;
        setShowAuditModal(false);
        setAuditLabel('');
        setModalHeadingLabel('');
        dispatch(clearAppointmentAuditData());
    };

    const onUpdate = (key, value) => {
        clearError(key);
        setIsDisabledStartAppointmentButton(true);
        setIsDisabledUpdateAppointmentButton(true);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateAppointmentType = (key, value, type) => {
        onUpdate(key, value);
        setNewEntry((prev) => ({ ...prev, type }));
    };

    const onUpdateLocation = (key, value, locationObj) => {
        clearError(key);
        clearError('remoteCallUrl');
        const remoteCallUrl = !locationObj?.remote ? '' : newEntry.remoteCallUrl;
        const additionalDetails =
            locationObj?.name?.toLowerCase() !== 'community' ? '' : newEntry.additionalDetails;
        setNewEntry((prev) => ({
            ...prev,
            locationObj,
            remoteCallUrl,
            additionalDetails,
            [key]: value
        }));
    };

    const onUpdateTime = (key, value) => {
        clearError(key === 'endTime' ? 'startTime' : 'endTime');
        onUpdate(key, value);
    };

    const onDelete = () => {
        dispatch(deleteUserCalendarAppointment(newEntry.userId, newEntry.type, newEntry.id));
    };

    const onSearchAuditItems = (label, modalHeadingLabel) => {
        if (newEntry.id) dispatch(loadAppointmentAuditData('appointment', newEntry.id, label));
        setShowAuditModal(true);
        setAuditLabel(label);
        setModalHeadingLabel(modalHeadingLabel ?? label);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsDisabledSaveButton(true);
        const {
            /* eslint-disable no-unused-vars */
            type,
            service,
            location,
            locationObj,
            participant,
            remoteCallUrl,
            ...rest
        } = newEntry;
        const updatedEntry = { ...rest, remoteCallUrl };
        const newErrors = validate(
            updatedEntry,
            initialStateOnEntry,
            currentParticipantEntry?.referralDate,
            roles
        );
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        const payload = { ...rest, remoteCallUrl: remoteCallUrl ? encodeURI(remoteCallUrl) : null };
        const hash = createHash();
        setHash(hash);
        dispatch(
            updateUserCalendarAppointment(
                usersCurrentAppointment.userId,
                newEntry.type,
                payload,
                hash
            )
        );
        setInitialStateOnEntry(payload);
    };

    // RENDER
    return (
        <>
            <Panel width="500px" open={openAppointmentSchedulerPanel} onToggle={onPanelClose}>
                <div className={sidePanel.panelActions}>
                    <Switch
                        id={'auditDisplayFields'}
                        label="Show Audit Fields"
                        onOff={true}
                        customClass="auditSwitch"
                        checked={isAuditable}
                        onChange={(e) => setIsAuditable(e.target.checked)}
                    />
                    <OnDeleteIcon
                        onDelete={onDelete}
                        roles={roles}
                        acceptedRoles={deleteRoles}
                        active={!!newEntry.id}
                    />
                </div>

                <div className={sidePanel.formWrapper}>
                    <form onSubmit={onSubmit} data-testid="user_appointment_edit_form">
                        <StaticField content={newEntry.service} customClass="underlinedContent" />

                        {canEditAppointment(isAppointmentKnown, 'adviser', roles, isInPast) ? (
                            <AdviserSelect
                                userId={newEntry.userId || ''}
                                serviceId={newEntry.serviceId}
                                initialAdviser={currentCalendarOwner}
                                onAdviserChange={(id) => onUpdate('userId', id)}
                                disabled={!newEntry.service}
                                mandatory={true}
                                errors={errors}
                            />
                        ) : (
                            <StaticField
                                id="adviser"
                                label="Adviser"
                                content={adviser?.loginEmailAddress || ''}
                            />
                        )}

                        {canEditAppointment(
                            isAppointmentKnown,
                            'appointmentType',
                            roles,
                            isInPast
                        ) ? (
                            <AppointmentTypeSelect
                                appointmentTypes={filteredAppointmentTypes}
                                appointmentTypeId={newEntry.typeId}
                                onAppointmentTypeChange={onUpdateAppointmentType}
                                errors={errors}
                            />
                        ) : (
                            <StaticField
                                id="type"
                                label="Appointment Type"
                                content={newEntry.type}
                            />
                        )}

                        {canEditAppointment(isAppointmentKnown, 'location', roles, isInPast) ? (
                            <>
                                <LocationSelect
                                    locationId={newEntry.locationId}
                                    serviceId={newEntry.serviceId}
                                    errors={errors}
                                    onLocationChange={onUpdateLocation}
                                    isAuditable={isAuditable}
                                    onSearchAuditItems={onSearchAuditItems}
                                />
                                {newEntry.locationObj?.remote && (
                                    <TextInputField
                                        id={'remoteCallUrl'}
                                        label={'Remote Call URL'}
                                        placeholder={'Enter a remote call URL'}
                                        maxLength={MAX_URL}
                                        value={newEntry.remoteCallUrl || ''}
                                        error={errors.remoteCallUrl}
                                        onChange={(e) =>
                                            onUpdate('remoteCallUrl', e.target.value.toLowerCase())
                                        }
                                    />
                                )}
                                {newEntry?.locationObj?.name?.toLowerCase() === 'community' && (
                                    <TextInputField
                                        id="additionalDetails"
                                        label="Additional Details"
                                        placeholder="Enter additional details..."
                                        maxLength={ADDITIONAL_DETAILS_LIMIT}
                                        value={newEntry.additionalDetails || ''}
                                        onChange={(e) =>
                                            onUpdate('additionalDetails', e.target.value)
                                        }
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                <StaticField
                                    id="location"
                                    label="Location"
                                    content={newEntry.location}
                                    isAuditable={isAuditable}
                                    onSearchAuditItems={onSearchAuditItems}
                                />

                                {newEntry.remoteCallUrl && (
                                    <StaticField
                                        id="remoteCallUrl"
                                        label="Remote Call URL"
                                        content={newEntry.remoteCallUrl}
                                    />
                                )}

                                {newEntry.additionalDetails && (
                                    <StaticField
                                        id="additionalDetails"
                                        label="Additional Details"
                                        content={newEntry.additionalDetails}
                                    />
                                )}
                            </>
                        )}

                        {canEditAppointment(
                            isAppointmentKnown,
                            'startAndEndTime',
                            roles,
                            isInPast
                        ) ? (
                            <StartAndEndTimesSelect
                                startTime={newEntry.startTime}
                                endTime={newEntry.endTime}
                                onTimeChange={onUpdateTime}
                                errors={errors}
                                isAuditable={isAuditable}
                                onSearchAuditItems={onSearchAuditItems}
                            />
                        ) : (
                            <>
                                <StaticField
                                    id="startTime"
                                    label="Start Time"
                                    content={newEntry.startTime}
                                    isAuditable={isAuditable}
                                    onSearchAuditItems={() =>
                                        onSearchAuditItems('Start Date', 'Start Time')
                                    }
                                />
                                <StaticField
                                    id="endTime"
                                    label="End Time"
                                    content={newEntry.endTime}
                                    isAuditable={isAuditable}
                                    onSearchAuditItems={() =>
                                        onSearchAuditItems('End Date', 'End Time')
                                    }
                                />
                            </>
                        )}

                        {canEditAppointment(isAppointmentKnown, 'date', roles, isInPast) ? (
                            <DateSelect
                                mandatory={true}
                                value={newEntry.date}
                                error={errors.date}
                                onDateChange={(date) => onUpdate('date', date)}
                                isAuditable={isAuditable}
                                onSearchAuditItems={() => onSearchAuditItems('Start Date', 'Date')}
                            />
                        ) : (
                            <StaticField
                                id="date"
                                label="Date"
                                content={reverseFormatDate(newEntry.date)}
                                isAuditable={isAuditable}
                                onSearchAuditItems={() => onSearchAuditItems('Start Date', 'Date')}
                            />
                        )}

                        <StaticParticipant
                            participant={currentParticipantEntry}
                            hasParticipantAccess={hasParticipantAccess}
                            onNav={onPanelClose}
                        />
                        <MandatoryActivitiesSwitch
                            isMandatory={newEntry.mandatory}
                            onSwitch={onUpdate}
                            disabled={true}
                        />

                        <div className={sidePanel.updateButton}>
                            <Button
                                id="appointmentEditButton"
                                content="Update"
                                disabled={
                                    !canEditAppointment(
                                        isAppointmentKnown,
                                        'updateButton',
                                        roles,
                                        isInPast
                                    ) || isDisabledSaveButton
                                }
                                error={errors.button}
                                clearError={() => clearError('button')}
                                customErrorClass="rightSideButtonError"
                            />
                        </div>

                        {newEntry.id && currentParticipantEntry?.firstName && (
                            <StartAppointmentRow
                                onPanelClose={onPanelClose}
                                disabled={isDisabledStartAppointmentButton}
                                isUpdateDisabled={isDisabledUpdateAppointmentButton}
                                hasParticipantAccess={hasParticipantAccess}
                                appointment={newEntry}
                                participant={currentParticipantEntry}
                                isAuditable={isAuditable}
                                onSearchAuditItems={onSearchAuditItems}
                            />
                        )}
                    </form>
                </div>
            </Panel>
            {showAuditModal && (
                <div id="audit">
                    <AuditAppointmentModal
                        isOpen={showAuditModal}
                        label={auditLabel}
                        modalHeadingLabel={modalHeadingLabel}
                        onClose={onAuditModalClose}
                    />
                </div>
            )}
        </>
    );
};

UserAppointmentEdit.propTypes = {
    usersCurrentAppointment: PropTypes.object,
    currentCalendarOwner: PropTypes.object,
    currentLocation: PropTypes.string,
    currentParticipantEntry: PropTypes.object
};

export default UserAppointmentEdit;
